import {useEffect, useRef} from 'react';

interface TrackableActor {
  getSnapshot: () => {value: unknown};
  subscribe: (listener: (snapshot: {value: unknown}) => void) => {
    unsubscribe: () => void;
  };
}

// Subscribes to an xstate actor and fires `onEnter` only when the step (state
// value) actually changes — not on every transition. UPDATE_FIELD fires a
// transition per keystroke, so the value-change guard is what keeps analytics
// from flooding.
export const useTrackStepEntry = (
  actor: TrackableActor,
  onEnter: (stepName: string) => void,
): void => {
  // Stable ref so callers don't have to memoize `onEnter` to keep the
  // subscription from tearing down on every render.
  const onEnterRef = useRef(onEnter);

  // effect:audited — mirror the latest `onEnter` into the ref off-render so the
  // subscription always invokes the current callback without re-subscribing.
  // Writing the ref during render is unsafe: React can discard that render.
  useEffect(() => {
    onEnterRef.current = onEnter;
  }, [onEnter]);

  // effect:audited — syncs with xstate actor's pub/sub; needs unsubscribe
  // cleanup on unmount and re-subscription when the `actor` identity changes.
  useEffect(() => {
    let previousValue: unknown;

    const handle = (snapshot: {value: unknown}): void => {
      if (snapshot.value === previousValue) return;

      previousValue = snapshot.value;
      onEnterRef.current(snapshot.value as string);
    };

    handle(actor.getSnapshot());

    const subscription = actor.subscribe(handle);

    return () => subscription.unsubscribe();
  }, [actor]);
};
